import { compose } from 'redux';
import { property, curry, findKey, partialRight } from 'lodash-es';
import { jsonApi } from '../http-request';
import { map, propEq, propFromSelector } from '../utils';
import { selectors as item } from '../item';
import { user } from '../current/current.selectors';
import isAfter from 'date-fns/isAfter';
import differenceInDays from 'date-fns/differenceInDays';

const collection = jsonApi.selectors.collection('moves');
const findMove = jsonApi.findResource(collection);

const findMoveAndHydrate = curry((fn, state) =>
  compose(
    jsonApi.utils.hydrate(state),
    findMove(fn)
  )(state)
);

export const all = state =>
  compose(
    map(jsonApi.utils.hydrate(state)),
    collection
  )(state);

const createFacade = moveSelector => {
  const propFromMove = propFromSelector(moveSelector);

  const id = propFromMove('id');
  const items = propFromMove('items');
  const date = propFromMove('date');
  const fromAddress = propFromMove('from_address');
  const toAddress = propFromMove('to_address');
  const direction = propFromMove('direction');
  // need_verify is true if the move has items that are unverified.
  // This is used to display messages only when there are items
  // that have been created, but not verified.
  const needToVerify = propFromMove('need_verify');
  // is_verified is true if the move has been verified, false otherwise
  const isVerified = propFromMove('is_verified');
  const hasDate = compose(
    Boolean,
    date
  );
  const isValid = state => hasValidAddresses(state) && hasDate(state);

  const hasOwnership = state => !!ownership(toAddress(state));

  const hasValidAddresses = state =>
    isValidAddress(fromAddress(state)) && isValidAddress(toAddress(state));

  const declinedItemTypes = propFromMove('declined_item_types');
  const declinedStatusByCode = (code, state) =>
    compose(
      partialRight(findKey, codes => codes.indexOf(code) !== -1),
      declinedItemTypes
    )(state);

  const isMilitary = state =>
    isMilitaryAddress(fromAddress(state)) ||
    isMilitaryAddress(toAddress(state));
  const todayIsAfterDate = state => {
    const moveDate = new Date(date(state));
    return isAfter(Date.now(), moveDate);
  };
  const signedUpAfterDate = state => {
    const moveDate = new Date(date(state));
    const signUpDate = new Date(user(state).signedUpAt(state));
    return isAfter(signUpDate, moveDate);
  };
  const daysTillDate = state => {
    const moveDate = new Date(date(state));
    return differenceInDays(moveDate, new Date());
  };
  const distanceFromSignUpToDate = state => {
    const moveDate = new Date(date(state));
    const signUpDate = new Date(user(state).signedUpAt(state));
    return differenceInDays(moveDate, signUpDate);
  };

  return {
    date,
    todayIsAfterDate,
    signedUpAfterDate,
    daysTillDate,
    distanceFromSignUpToDate,
    id,
    identity: moveSelector,
    items: compose(
      map(item.byId),
      map(property('id')),
      items
    ),
    fromAddress,
    toAddress,
    direction,
    needToVerify,
    isVerified,
    hasDate,
    isValid,
    hasOwnership,
    declinedStatusByCode,
    isMilitary,
  };
};

export const byId = moveId =>
  createFacade(findMoveAndHydrate(propEq('id', String(moveId))));

const VALID_ADDRESS_STATUSES = ['valid', 'forced'];
const isValidAddress = address =>
  !!address && VALID_ADDRESS_STATUSES.includes(address.status);
const isMilitaryAddress = address =>
  !!address && address.classification === 'military';
const ownership = address => !!address && address.ownership;
